import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { TreeStructureIcon as TreeStructure, ScissorsIcon as Scissors, StorefrontIcon as Storefront, TruckIcon as Truck, HeartIcon as Heart, MagnifyingGlassIcon as MagnifyingGlass, AirplaneTiltIcon as AirplaneTilt, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

const HAT_ICON = {
  breeder: TreeStructure,
  dog_owner: Heart,
  service_provider: Scissors,
  storefront_seller: Storefront,
  transporter: Truck,
  international_transporter: AirplaneTilt,
  buyer: MagnifyingGlass,
};

/**
 * MultiHatCards — compact clickable summary strip for members who wear
 * more than one hat (e.g., breeder + trainer). The primary persona keeps
 * its full dashboard; each extra hat gets one card with a stat or two
 * and a deep link into that hat's tools. Renders nothing for
 * single-persona members, so the default design is untouched.
 */
export const MultiHatCards = () => {
  const [hats, setHats] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get("/me/multi-hat-summary", { silent: true })
      .then((r) => { if (!cancelled) setHats(r.data?.hats || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!hats.length) return null;

  return (
    <div className="mb-8" data-testid="multi-hat-cards">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-3">
        Your other hats
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {hats.map((h) => {
          const Icon = HAT_ICON[h.persona] || TreeStructure;
          return (
            <Link
              key={h.persona}
              to={h.link}
              onClick={() => track("dashboard:multi_hat_card_clicked", { hat: h.persona })}
              className="group rounded-xl border border-[#E5E5E5] bg-white p-4 flex items-center gap-3 hover:border-ink/40 hover:shadow-sm transition-all"
              data-testid={`multi-hat-card-${h.persona}`}
            >
              <span className="w-10 h-10 rounded-full bg-[#FBF7EC] border border-[#E8D9A6] flex items-center justify-center flex-shrink-0">
                <Icon size={18} weight="duotone" className="text-gold-dark" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[13px] font-semibold text-ink truncate">{h.label}</span>
                <span className="block text-[11px] text-[#6B6B6B] truncate">
                  {(h.stats || []).join(" · ")}
                </span>
              </span>
              <span className="text-[11px] font-semibold text-gold-dark flex items-center gap-1 flex-shrink-0 group-hover:translate-x-0.5 transition-transform">
                {h.cta} <ArrowRight size={12} weight="bold" />
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default MultiHatCards;
